// Widget „Verknüpft“ (Handoff §6.3): Zuständig (Stift = Auswahl, „Empfohlen: X · Übernehmen“), Objekt
// (→ öffnen, Stift = zuweisen) + Einheit, Mieter (das Ticket kennt genau einen: mieter_id), E-Mails mit ×.
// Änderungen gehen sofort über speichereFelder (gleicher Schreibweg wie Speichern).
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Buildings, Crown, EnvelopeSimple, HouseLine, LinkSimple, PencilSimple, Plus, Sparkle, UserCircle, UserPlus, Users, UsersThree, X, type Icon,
} from '@phosphor-icons/react';
import { WidgetHuelle } from '../../../../components/ui/WidgetHuelle';
import { resolveZustaendig } from '../../../../lib/rechnungen/zustaendigkeit';
import type { Ticket, TicketEditData } from '../types';
import { FertigKnopf, LABEL, RundKlein, SuchAuswahl, useEigentuemerVorschlaege } from './widgetTeile';
import { KleinAvatar, OPTION_STIL } from '../liste/teile';

export interface WidgetVerknuepftProps {
  ticket: Ticket;
  teamMembers: { id: string; full_name: string }[];
  teams: { id: string; name: string }[];
  meineId: string | null;
  einheit: string | null;
  bearbeiterVorschlag: string | null;
  emails: { id: string; betreff: string | null }[];
  onSpeichern: (teil: Partial<TicketEditData>) => void;
  onObjekt?: (id: string) => void;
  onMieter?: (id: string) => void;
  onEmailLoesen: (id: string) => void;
}

type Offen = 'zust' | 'objekt' | 'mieter' | 'eigentuemer' | null;

function Zeile({ icon: I, farbe, label, wert, leer, unter, offen, onWert, onStift, stiftTip, onPfeil, pfeilTip, kinder }: {
  icon: Icon; farbe: string; label: string; wert: string | null; leer: string; unter?: string | null; offen: boolean;
  onWert: () => void; onStift: () => void; stiftTip: string; onPfeil?: () => void; pfeilTip?: string; kinder?: ReactNode;
}) {
  return (
    <div className="flex flex-col" style={{ gap: 8, padding: '8px 8px 8px 10px', borderRadius: 16, background: offen ? 'var(--fill2)' : 'var(--fill)',
      boxShadow: offen ? `inset 0 0 0 2px ${farbe}` : 'none', transition: 'background .15s' }}>
      <div className="flex min-w-0 items-center" style={{ gap: 8 }}>
        <button type="button" onClick={onWert} data-tip={onPfeil && wert ? pfeilTip : stiftTip}
          className="flex min-w-0 flex-1 items-center text-left" style={{ gap: 10, border: 'none', padding: 0, background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: `color-mix(in srgb, ${farbe} 15%, transparent)`, color: farbe }}>
            <I size={17} weight="fill" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
            <span style={LABEL}>{label}</span>
            <span className="truncate" style={{ fontSize: 14, fontWeight: 800, color: wert ? 'var(--ink)' : 'var(--ink3)' }}>{wert ?? leer}</span>
            {unter && <span className="truncate" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>{unter}</span>}
          </span>
        </button>
        <RundKlein icon={PencilSimple} label={stiftTip.split(' – ')[0]} tip={stiftTip} onClick={onStift} />
        {onPfeil && wert && <RundKlein icon={ArrowRight} label={pfeilTip ?? 'Öffnen'} tip={pfeilTip} farbe={farbe} onClick={onPfeil} />}
      </div>
      {kinder}
    </div>
  );
}

export function WidgetVerknuepft({ ticket, teamMembers, teams, meineId, einheit, bearbeiterVorschlag, emails, onSpeichern, onObjekt, onMieter, onEmailLoesen }: WidgetVerknuepftProps) {
  const [offen, setOffen] = useState<Offen>(null);
  const navigate = useNavigate();
  const eigVorschlaege = useEigentuemerVorschlaege(ticket.objekt_id, ticket.einheit_id, offen === 'eigentuemer');
  const eigRel = ticket.eigentuemer_rel;
  const eigentuemerName = eigRel ? ((eigRel.firma_name ?? '').trim() || `${eigRel.vorname ?? ''} ${eigRel.nachname ?? ''}`.trim() || 'Eigentümer') : null;
  const umschalten = (k: Exclude<Offen, null>) => setOffen((o) => (o === k ? null : k));

  const z = resolveZustaendig(ticket.zustaendig_id, ticket.zustaendig_typ ?? null, teamMembers, teams);
  const zustUnter = z ? (ticket.zustaendig_id === meineId ? 'Dir zugewiesen' : z.isTeam ? 'Team' : null) : null;
  const zeigeVorschlag = !!ticket.bearbeiter_vorschlag_id && ticket.bearbeiter_vorschlag_id !== ticket.zustaendig_id;
  const mieterName = ticket.mieter_rel ? `${ticket.mieter_rel.vorname} ${ticket.mieter_rel.nachname}`.trim() : null;
  const zustTip = z ? 'Zuständig – Mitarbeiter oder Team ändern' : 'Zuständig – Mitarbeiter oder Team zuweisen';
  const objektTip = ticket.objekt ? 'Objekt ändern – Andere Immobilie zuweisen' : 'Objekt zuweisen – Betroffene Immobilie verknüpfen';

  return (
    <WidgetHuelle titel="Verknüpft" icon={LinkSimple} wasserzeichen={LinkSimple} style={{ flexShrink: 0 }}>
      <div className="flex flex-col" style={{ gap: 8 }}>
        <Zeile icon={z ? (z.isTeam ? UsersThree : UserCircle) : UserPlus} farbe="var(--acc)" label="Zuständig" wert={z?.name ?? null} leer="+ Zuweisen" unter={zustUnter}
          offen={offen === 'zust'} onWert={() => umschalten('zust')} onStift={() => umschalten('zust')} stiftTip={zustTip}
          kinder={offen === 'zust' && (
            <div className="flex" style={{ gap: 6 }}>
              <select autoFocus value={ticket.zustaendig_id || ''} aria-label="Zuständig" data-tip="Zuständig – Mitarbeiter oder Team"
                onChange={(e) => {
                  const id = e.target.value;
                  const typ: 'mitarbeiter' | 'team' | null = id ? (teams.some((t) => t.id === id) ? 'team' : 'mitarbeiter') : null;
                  onSpeichern({ zustaendig_id: id, zustaendig_typ: typ });
                  setOffen(null);
                }}
                style={{ flex: 1, minWidth: 0, height: 36, padding: '0 8px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700 }}>
                <option style={OPTION_STIL} value="">– Niemand –</option>
                <optgroup style={OPTION_STIL} label="Mitarbeiter">{teamMembers.map((m) => <option style={OPTION_STIL} key={m.id} value={m.id}>{m.full_name}</option>)}</optgroup>
                {teams.length > 0 && <optgroup style={OPTION_STIL} label="Teams">{teams.map((t) => <option style={OPTION_STIL} key={t.id} value={t.id}>{t.name}</option>)}</optgroup>}
              </select>
              <FertigKnopf onClick={() => setOffen(null)} />
            </div>
          )} />
        {zeigeVorschlag && (
          <div className="flex items-center" style={{ gap: 8, padding: '6px 8px', borderRadius: 12, background: 'color-mix(in srgb, #8a63d2 10%, transparent)' }}>
            <Sparkle size={13} weight="fill" color="#8a63d2" className="shrink-0" />
            <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12 }} data-tip="Bearbeiter-Vorschlag – Von der KI empfohlene zuständige Person">
              <span style={{ color: 'var(--ink3)' }}>Empfohlen:</span> <b>{bearbeiterVorschlag || '…'}</b>
            </span>
            <button type="button" data-tip="Übernehmen – Empfohlenen Bearbeiter als zuständig eintragen"
              onClick={() => onSpeichern({ zustaendig_id: ticket.bearbeiter_vorschlag_id!, zustaendig_typ: ticket.bearbeiter_vorschlag_typ === 'team' ? 'team' : 'mitarbeiter' })}
              style={{ height: 26, padding: '0 10px', borderRadius: 13, border: 'none', cursor: 'pointer', background: '#8a63d2', color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 800 }}>
              Übernehmen
            </button>
          </div>
        )}
      </div>

      <Zeile icon={Buildings} farbe="var(--acc)" label="Objekt" wert={ticket.objekt?.bezeichnung ?? null} leer="+ Objekt verknüpfen"
        unter={ticket.objekt ? [ticket.objekt.strasse, ticket.objekt.hausnummer].filter(Boolean).join(' ') || null : null}
        offen={offen === 'objekt'} stiftTip={objektTip} pfeilTip="Zum Objekt – Objektseite öffnen"
        onWert={() => { if (ticket.objekt && onObjekt) onObjekt(ticket.objekt.id); else umschalten('objekt'); }}
        onStift={() => umschalten('objekt')}
        onPfeil={ticket.objekt && onObjekt ? () => onObjekt(ticket.objekt!.id) : undefined}
        kinder={offen === 'objekt' && (
          <div className="flex items-start" style={{ gap: 6 }}>
            <SuchAuswahl imFluss art="objekt" autoFokus onWahl={(t) => { onSpeichern({ objekt_id: t.id }); setOffen(null); }} />
            {ticket.objekt && <RundKlein icon={X} label="Objekt entfernen" onClick={() => { onSpeichern({ objekt_id: '' }); setOffen(null); }} />}
            <FertigKnopf onClick={() => setOffen(null)} />
          </div>
        )} />
      {(einheit || ticket.einheit_id) && (
        <div className="flex min-w-0 items-center" style={{ gap: 8, padding: '0 12px', fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}>
          <HouseLine size={14} weight="fill" color="var(--ink3)" className="shrink-0" />
          <span className="truncate">{einheit || 'Einheit'}</span>
        </div>
      )}

      {/* Mieter: das Datenmodell kennt einen Mieter je Ticket (tickets.mieter_id) */}
      <div className="flex flex-col" style={{ gap: 6, padding: '8px 8px 8px 10px', borderRadius: 16, background: offen === 'mieter' ? 'var(--fill2)' : 'var(--fill)',
        boxShadow: offen === 'mieter' ? 'inset 0 0 0 2px var(--info)' : 'none' }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'color-mix(in srgb, var(--info) 15%, transparent)', color: 'var(--info)' }}>
            <Users size={17} weight="fill" />
          </span>
          <span className="flex-1" style={LABEL}>Mieter</span>
          {!ticket.mieter_id && (
            <RundKlein icon={Plus} label="Mieter verknüpfen" tip="Mieter hinzufügen – Mieter mit dem Ticket verknüpfen" onClick={() => umschalten('mieter')} />
          )}
        </div>
        {mieterName && ticket.mieter_rel && (
          <div className="flex min-w-0 items-center" style={{ gap: 6 }}>
            <button type="button" onClick={() => onMieter?.(ticket.mieter_rel!.id)} data-tip={`${mieterName} – Mieterprofil öffnen`}
              className="immo-zeile-hover flex min-w-0 flex-1 items-center text-left"
              style={{ gap: 8, padding: '6px 8px', borderRadius: 12, border: 'none', background: 'var(--bg)', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
              <KleinAvatar name={mieterName} groesse={24} />
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 800 }}>{mieterName}</span>
              <ArrowRight size={12} weight="bold" color="var(--ink3)" />
            </button>
            <RundKlein icon={X} groesse={26} label="Entfernen" tip="Entfernen – Mieter vom Ticket lösen" onClick={() => onSpeichern({ mieter_id: '' })} />
          </div>
        )}
        {!ticket.mieter_id && offen !== 'mieter' && (
          <button type="button" onClick={() => umschalten('mieter')}
            style={{ alignSelf: 'flex-start', border: 'none', padding: '2px 0', background: 'transparent', color: 'var(--ink3)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700 }}>
            + Mieter verknüpfen
          </button>
        )}
        {offen === 'mieter' && (
          <div className="flex items-start" style={{ gap: 6 }}>
            <SuchAuswahl imFluss art="mieter" autoFokus onWahl={(t) => { onSpeichern({ mieter_id: t.id }); setOffen(null); }} />
            <FertigKnopf onClick={() => setOffen(null)} />
          </div>
        )}
      </div>

      {/* Eigentümer (#513): ein Eigentümer je Ticket (tickets.eigentuemer_id), z. B. bei Meldungen in einer WEG */}
      <div className="flex flex-col" style={{ gap: 6, padding: '8px 8px 8px 10px', borderRadius: 16, background: offen === 'eigentuemer' ? 'var(--fill2)' : 'var(--fill)',
        boxShadow: offen === 'eigentuemer' ? 'inset 0 0 0 2px var(--acc)' : 'none' }}>
        <div className="flex items-center" style={{ gap: 10 }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'color-mix(in srgb, var(--acc) 15%, transparent)', color: 'var(--acc)' }}>
            <Crown size={17} weight="fill" />
          </span>
          <span className="flex-1" style={LABEL}>Eigentümer</span>
          {!ticket.eigentuemer_id && (
            <RundKlein icon={Plus} label="Eigentümer verknüpfen" tip="Eigentümer hinzufügen – Eigentümer mit dem Ticket verknüpfen" onClick={() => umschalten('eigentuemer')} />
          )}
        </div>
        {ticket.eigentuemer_id && eigentuemerName && (
          <div className="flex min-w-0 items-center" style={{ gap: 6 }}>
            <button type="button" onClick={() => navigate(`/admin/eigentuemer/${ticket.eigentuemer_id}`)} data-tip={`${eigentuemerName} – Eigentümerprofil öffnen`}
              className="immo-zeile-hover flex min-w-0 flex-1 items-center text-left"
              style={{ gap: 8, padding: '6px 8px', borderRadius: 12, border: 'none', background: 'var(--bg)', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
              <KleinAvatar name={eigentuemerName} groesse={24} />
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 800 }}>{eigentuemerName}</span>
              <ArrowRight size={12} weight="bold" color="var(--ink3)" />
            </button>
            <RundKlein icon={X} groesse={26} label="Entfernen" tip="Entfernen – Eigentümer vom Ticket lösen" onClick={() => onSpeichern({ eigentuemer_id: '' })} />
          </div>
        )}
        {!ticket.eigentuemer_id && offen !== 'eigentuemer' && (
          <button type="button" onClick={() => umschalten('eigentuemer')}
            style={{ alignSelf: 'flex-start', border: 'none', padding: '2px 0', background: 'transparent', color: 'var(--ink3)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700 }}>
            + Eigentümer verknüpfen
          </button>
        )}
        {offen === 'eigentuemer' && (
          <div className="flex items-start" style={{ gap: 6 }}>
            <SuchAuswahl imFluss art="eigentuemer" autoFokus vorschlaege={eigVorschlaege} onWahl={(t) => { onSpeichern({ eigentuemer_id: t.id }); setOffen(null); }} />
            <FertigKnopf onClick={() => setOffen(null)} />
          </div>
        )}
      </div>

      {emails.map((m) => (
        <div key={m.id} className="flex min-w-0 items-center" style={{ gap: 10, padding: '8px 8px 8px 10px', borderRadius: 16, background: 'var(--fill)' }}>
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: 'color-mix(in srgb, var(--warn) 15%, transparent)', color: 'var(--warn)' }}>
            <EnvelopeSimple size={17} weight="fill" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
            <span style={LABEL}>E-Mail</span>
            <span className="truncate" style={{ fontSize: 13, fontWeight: 700 }}>{m.betreff || 'E-Mail'}</span>
          </span>
          <RundKlein icon={X} groesse={26} label="E-Mail-Verknüpfung aufheben" tip="Verknüpfung aufheben – Die E-Mail bleibt im Postfach erhalten" onClick={() => onEmailLoesen(m.id)} />
        </div>
      ))}
    </WidgetHuelle>
  );
}
